import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { ltvService } from '@/services/ltvService';
import type { LtvTier, PatientLtv, CampaignLtv, ChannelLtv, ProcedureLtv } from '@/types/ltv';
import styles from './PatientLtvPage.module.css';

const PAGE_SIZE = 20;

type TabKey = 'patients' | 'campaigns' | 'channels' | 'procedures';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'patients', label: 'Pacientes' },
  { key: 'campaigns', label: 'Campanhas' },
  { key: 'channels', label: 'Canais' },
  { key: 'procedures', label: 'Procedimentos' },
];

const TIER_LABELS: Record<LtvTier, string> = {
  VIP: 'VIP',
  PREMIUM: 'Premium',
  REGULAR: 'Regular',
  LOW_VALUE: 'Baixo Valor',
};

const TIER_CSS: Record<LtvTier, string> = {
  VIP: styles.tierVIP,
  PREMIUM: styles.tierPREMIUM,
  REGULAR: styles.tierREGULAR,
  LOW_VALUE: styles.tierLOW_VALUE,
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * LTV (Lifetime Value) analytics page with tabbed navigation.
 *
 * Tabs:
 * - Pacientes: paginated table with tier filter
 * - Campanhas: campaign revenue aggregation
 * - Canais: channel revenue aggregation
 * - Procedimentos: procedure revenue aggregation
 *
 * Route: /admin/ltv
 */
export function PatientLtvPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('patients');
  const [currentPage, setCurrentPage] = useState(1);
  const [tierFilter, setTierFilter] = useState<LtvTier | ''>('');

  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
  }, []);

  const handleTierChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setTierFilter(e.target.value as LtvTier | '');
      setCurrentPage(1);
    },
    [],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <TrendingUp size={24} aria-hidden="true" /> LTV - Lifetime Value
        </h1>
        <p className={styles.subtitle}>
          Analise o valor vitalicio dos pacientes, campanhas, canais e procedimentos.
        </p>
      </header>

      {/* Tab bar */}
      <div className={styles.tabBar} role="tablist" aria-label="Abas de LTV">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      {activeTab === 'patients' && (
        <PatientsTab
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          tierFilter={tierFilter}
          onTierChange={handleTierChange}
        />
      )}
      {activeTab === 'campaigns' && <CampaignsTab />}
      {activeTab === 'channels' && <ChannelsTab />}
      {activeTab === 'procedures' && <ProceduresTab />}
    </div>
  );
}

/* ---------- Patients Tab ---------- */

interface PatientsTabProps {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  tierFilter: LtvTier | '';
  onTierChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

function PatientsTab({ currentPage, setCurrentPage, tierFilter, onTierChange }: PatientsTabProps) {
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ltv-patients', currentPage, tierFilter],
    queryFn: () =>
      ltvService.getPatients({
        page: currentPage,
        limit: PAGE_SIZE,
        tier: tierFilter || undefined,
      }),
  });

  if (error) {
    return (
      <div
        className={styles.errorState}
        role="alert"
        id="tabpanel-patients"
        aria-labelledby="tab-patients"
      >
        <AlertCircle size={32} aria-hidden="true" />
        <p>Erro ao carregar dados de LTV dos pacientes.</p>
      </div>
    );
  }

  const patients: PatientLtv[] = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  return (
    <div role="tabpanel" id="tabpanel-patients" aria-labelledby="tab-patients">
      {/* Filters */}
      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={tierFilter}
          onChange={onTierChange}
          aria-label="Filtrar por tier"
        >
          <option value="">Todos os tiers</option>
          <option value="VIP">VIP</option>
          <option value="PREMIUM">Premium</option>
          <option value="REGULAR">Regular</option>
          <option value="LOW_VALUE">Baixo Valor</option>
        </select>
      </div>

      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
        </div>
      ) : patients.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum paciente encontrado.</p>
        </div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th className={styles.textRight}>Consultas</th>
                  <th className={styles.textRight}>Ticket Medio</th>
                  <th className={styles.textRight}>Receita Total</th>
                  <th className={styles.textRight}>LTV Estimado</th>
                  <th>Tier</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient) => (
                  <tr key={patient.patientId}>
                    <td>
                      <strong>{patient.patientName}</strong>
                      {patient.lastVisitDate && (
                        <>
                          <br />
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                            Ultima visita: {formatDate(patient.lastVisitDate)}
                          </span>
                        </>
                      )}
                    </td>
                    <td className={styles.textRight}>{patient.totalAppointments}</td>
                    <td className={styles.textRight}>{formatCurrency(patient.averageTicket)}</td>
                    <td className={styles.textRight}>{formatCurrency(patient.totalRevenue)}</td>
                    <td className={styles.textRight}>{formatCurrency(patient.estimatedLtv)}</td>
                    <td>
                      <span className={`${styles.tierBadge} ${TIER_CSS[patient.tier]}`}>
                        {TIER_LABELS[patient.tier]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                leftIcon={<ChevronLeft size={16} />}
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo}>
                Pagina {currentPage} de {totalPages} ({totalCount} pacientes)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                leftIcon={<ChevronRight size={16} />}
              >
                Proxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Campaigns Tab ---------- */

function CampaignsTab() {
  const { data: campaigns = [], isLoading, error } = useQuery({
    queryKey: ['ltv-campaigns'],
    queryFn: () => ltvService.getCampaigns(),
  });

  if (error) {
    return (
      <div
        className={styles.errorState}
        role="alert"
        id="tabpanel-campaigns"
        aria-labelledby="tab-campaigns"
      >
        <AlertCircle size={32} aria-hidden="true" />
        <p>Erro ao carregar dados de LTV por campanha.</p>
      </div>
    );
  }

  return (
    <div role="tabpanel" id="tabpanel-campaigns" aria-labelledby="tab-campaigns">
      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhuma campanha com dados de LTV.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campanha</th>
                <th className={styles.textRight}>Pacientes</th>
                <th className={styles.textRight}>Receita Total</th>
                <th className={styles.textRight}>LTV Medio</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign: CampaignLtv) => (
                <tr key={campaign.campaignId}>
                  <td><strong>{campaign.campaignName}</strong></td>
                  <td className={styles.textRight}>{campaign.totalPatients}</td>
                  <td className={styles.textRight}>{formatCurrency(campaign.totalRevenue)}</td>
                  <td className={styles.textRight}>{formatCurrency(campaign.averageLtv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Channels Tab ---------- */

function ChannelsTab() {
  const { data: channels = [], isLoading, error } = useQuery({
    queryKey: ['ltv-channels'],
    queryFn: () => ltvService.getChannels(),
  });

  if (error) {
    return (
      <div
        className={styles.errorState}
        role="alert"
        id="tabpanel-channels"
        aria-labelledby="tab-channels"
      >
        <AlertCircle size={32} aria-hidden="true" />
        <p>Erro ao carregar dados de LTV por canal.</p>
      </div>
    );
  }

  return (
    <div role="tabpanel" id="tabpanel-channels" aria-labelledby="tab-channels">
      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
        </div>
      ) : channels.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum canal com dados de LTV.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Canal</th>
                <th className={styles.textRight}>Pacientes</th>
                <th className={styles.textRight}>Receita Total</th>
                <th className={styles.textRight}>LTV Medio</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((channel: ChannelLtv) => (
                <tr key={channel.channel}>
                  <td><strong>{channel.channel}</strong></td>
                  <td className={styles.textRight}>{channel.totalPatients}</td>
                  <td className={styles.textRight}>{formatCurrency(channel.totalRevenue)}</td>
                  <td className={styles.textRight}>{formatCurrency(channel.averageLtv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Procedures Tab ---------- */

function ProceduresTab() {
  const { data: procedures = [], isLoading, error } = useQuery({
    queryKey: ['ltv-procedures'],
    queryFn: () => ltvService.getProcedures(),
  });

  if (error) {
    return (
      <div
        className={styles.errorState}
        role="alert"
        id="tabpanel-procedures"
        aria-labelledby="tab-procedures"
      >
        <AlertCircle size={32} aria-hidden="true" />
        <p>Erro ao carregar dados de LTV por procedimento.</p>
      </div>
    );
  }

  return (
    <div role="tabpanel" id="tabpanel-procedures" aria-labelledby="tab-procedures">
      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
          <Skeleton variant="rectangle" height="48px" />
        </div>
      ) : procedures.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum procedimento com dados de receita.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Procedimento</th>
                <th className={styles.textRight}>Preco Unit.</th>
                <th className={styles.textRight}>Pacientes</th>
                <th className={styles.textRight}>Consultas</th>
                <th className={styles.textRight}>Receita Total</th>
              </tr>
            </thead>
            <tbody>
              {procedures.map((proc: ProcedureLtv) => (
                <tr key={proc.procedureTypeId}>
                  <td><strong>{proc.procedureName}</strong></td>
                  <td className={styles.textRight}>
                    {proc.price != null ? formatCurrency(proc.price) : '-'}
                  </td>
                  <td className={styles.textRight}>{proc.totalPatients}</td>
                  <td className={styles.textRight}>{proc.totalAppointments}</td>
                  <td className={styles.textRight}>{formatCurrency(proc.totalRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
